<template>
  <div class="relative w-[1200px] max-w-full">
    <Loading v-if="isSubmitting" data-test="submitting" class="absolute inset-0 z-10 bg-white/70">
      <p class="text-base text-gray-800">Creating order...</p>
    </Loading>

    <Loading v-if="isLoading" class="py-32">
      <p class="text-base text-gray-800">Loading...</p>
    </Loading>

    <div v-else class="space-y-5">
      <section class="flex flex-wrap items-center gap-4">
        <figure class="text-center">
          <img
            :src="photoUrl"
            alt="Original photo"
            class="h-24 w-24 rounded-sm border border-gray-200 bg-[#FAFAFA] object-contain"
          />
          <figcaption class="mt-1 text-xs text-gray-500">Original</figcaption>
        </figure>
        <figure class="text-center">
          <img
            :src="previewImageUrl"
            alt="Preview"
            class="h-24 w-36 rounded-sm border border-gray-200 bg-[#FAFAFA] object-contain"
          />
          <figcaption class="mt-1 text-xs text-gray-500">Preview</figcaption>
        </figure>
        <div class="min-w-[240px] flex-1">
          <p class="text-xs uppercase tracking-wide text-gray-500">Product</p>
          <p data-test="product-name" class="text-lg font-bold">{{ productName }}</p>
        </div>
      </section>

      <section class="border-t pt-4">
        <h2 class="mb-3 text-base font-bold">Order</h2>
        <!-- A quick order entry is paid already, so it needs the transaction id; a quote
             is paid later, through the link in its e-mail. -->
        <MainOrderForm
          :values="form"
          :transactionIdRequired="!isQuote"
          :typeOptions="SYNC_ORDER_TYPES"
          :columns="2"
        />
      </section>

      <!-- One form for everything about the product: the add-on fields bind by name. The
           order's main information binds by v-model and stays outside it, so its fields
           cannot leak into the product. -->
      <FormKit v-model="product" type="form" :actions="false">
        <section class="space-y-4 border-t pt-4">
          <h2 class="text-base font-bold">Product</h2>
          <div class="grid grid-cols-[130px_1fr_130px_1fr] items-center gap-x-6">
            <FormLabel label="QTY" required>
              <FormKit name="qty" type="text" placeholder="Qty" />
            </FormLabel>
            <FormLabel label="Cost" required>
              <FormKit name="price" type="text" placeholder="Cost" />
            </FormLabel>
          </div>
          <!-- Engraving and background are what the canvas holds: only their prices can be
               set here, the options themselves change in the canvas. -->
          <div
            data-test="canvas-options"
            class="rounded-lg border border-hqPurple300 bg-[#fbf7f8] px-4 py-3"
          >
            <p class="text-sm font-medium text-gray-900">
              From the canvas
              <span class="font-normal text-gray-500">
                — to add, change or remove these, change them in the canvas.
              </span>
            </p>
            <div class="mt-3 grid grid-cols-[130px_1fr_130px_1fr] items-center gap-x-6 gap-y-2">
              <FormLabel label="Inscription">
                <p data-test="canvas-inscription" class="text-base">{{ inscriptionText }}</p>
              </FormLabel>
              <FormLabel v-if="hasInscription" label="Cost">
                <FormKit name="inscriptionPrice" type="text" placeholder="Cost" />
              </FormLabel>
              <template v-else><span /><span /></template>
              <FormLabel label="Background">
                <p data-test="canvas-background" class="text-base">
                  {{ product.background ? "Yes" : "No" }}
                </p>
              </FormLabel>
              <FormLabel v-if="product.background" label="Cost">
                <FormKit name="backgroundPrice" type="text" placeholder="Cost" />
              </FormLabel>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-x-8">
            <Collapse class="border-t py-3" title="Flag">
              <FormKit
                :classes="{ wrapper: 'max-w-full', outer: 'mt-3' }"
                name="flag"
                rows="4"
                type="textarea"
              />
            </Collapse>
            <Collapse class="border-t py-3" title="Light Bases">
              <NewProductLightBaseForm :values="product" />
            </Collapse>
            <Collapse class="border-t py-3" title="Metallic Plate">
              <NewProductMetalPlate :values="product" />
            </Collapse>
            <Collapse class="border-t py-3" title="3D Pop-up Card">
              <NewProduct3DPopUpCard :values="product" />
            </Collapse>
            <Collapse class="border-t py-3" title="Personal notes">
              <NewProductNotesForm :values="product" />
            </Collapse>
          </div>
        </section>
      </FormKit>

      <section class="flex flex-wrap items-center gap-6 border-t pt-4">
        <div class="grid flex-1 grid-cols-[130px_1fr_130px_1fr] items-center gap-x-6">
          <FormLabel label="Shipping" required>
            <FormKit
              v-model="additionalInfo.shipping_cost"
              name="shipping_cost"
              placeholder="Shipping cost"
              type="text"
            />
          </FormLabel>
          <FormLabel label="Tax" required>
            <FormKit v-model="additionalInfo.tax" name="tax" placeholder="Tax %" type="text" />
          </FormLabel>
        </div>
        <Button
          data-test="create-order"
          primaryButton
          leftIcon="check"
          leftIconColor="white"
          :text="isSubmitting ? 'Creating order...' : 'Create order'"
          :disabled="isSubmitting"
          @click="submit"
        />
      </section>
    </div>
  </div>
</template>

<script setup>
import { computed, toRefs } from "vue";
import { FormKit } from "@formkit/vue";
import MainOrderForm from "@/components/Order/OrderForms/MainOrderForm/MainOrderForm.vue";
import NewProduct3DPopUpCard from "@/components/Order/OrderForms/NewProductForm/NewProduct3DPopUpCard.vue";
import NewProductLightBaseForm from "@/components/Order/OrderForms/NewProductForm/NewProductLightBaseForm.vue";
import NewProductMetalPlate from "@/components/Order/OrderForms/NewProductForm/NewProductMetalPlate.vue";
import NewProductNotesForm from "@/components/Order/OrderForms/NewProductForm/NewProductNotesForm.vue";
import Collapse from "@/components/UI/Collapse/Collapse.vue";
import Button from "@/components/UI/Form/Button/Button.vue";
import FormLabel from "@/components/UI/Form/FormLabel.vue";
import Loading from "@/components/UI/Loading/Loading.vue";
import { useSyncPreviewOrder } from "./useSyncPreviewOrder";

const props = defineProps({
  // The pretty preview row being synced into an HQ order.
  preview: {
    type: Object,
    required: true,
  },
});

const { preview } = toRefs(props);
const {
  form,
  product,
  additionalInfo,
  productName,
  isLoading,
  isSubmitting,
  isQuote,
  SYNC_ORDER_TYPES,
  photoUrl,
  previewImageUrl,
  submit,
} = useSyncPreviewOrder(preview.value);

const hasInscription = computed(() =>
  Boolean(product.value.inscriptionFirst || product.value.inscriptionSecond)
);
const inscriptionText = computed(() =>
  hasInscription.value
    ? [product.value.inscriptionFirst, product.value.inscriptionSecond].filter(Boolean).join(" / ")
    : "None"
);
</script>
